import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import useFetchObjects from '../../api/useFetchObjects';
import ActionButton from '../common/ActionButton';
import DescriptionIcon from '@mui/icons-material/Description';
import { RTLFormControl, RTLDialogTitle, RTLDialogContent, RTLDialogActions } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

const DocumentSelectionDialog = ({ open, onClose, onDocumentSelected, excludedDocumentIds = [] }) => {
  const { t, i18n } = useTranslation(['tasks', 'documents', 'common']);
  const { user } = useAuth();
  const token = user?.token;
  const { isRTL, getRTLProps } = useRTL();
  
  const [documentId, setDocumentId] = useState('');
  const [error, setError] = useState(null);
  
  // Fetch documents
  const { 
    data: documentsData, 
    isLoading: documentsLoading,
    error: documentsError,
    refetch: refetchDocuments
  } = useFetchObjects('documents', { limit: 100 }, token);
  
  // Fetch existing task assignments to exclude already-assigned documents
  const { 
    data: taskAssignmentsData = { data: [] },
    isLoading: taskAssignmentsLoading,
    error: taskAssignmentsError,
    refetch: refetchAssignments
  } = useFetchObjects('task-assignments', { limit: 1000 }, token);
  
  // Display API fetch errors if they occur
  useEffect(() => {
    if (documentsError) {
      setError(`Error loading documents: ${documentsError.message}`);
    }
    if (taskAssignmentsError) {
      setError(`Error loading task assignments: ${taskAssignmentsError.message}`);
    }
  }, [documentsError, taskAssignmentsError]);
  
  // Reset form when opened
  useEffect(() => {
    if (open) {
      setDocumentId('');
      setError(null);
      refetchDocuments();
      refetchAssignments();
    }
  }, [open, refetchDocuments, refetchAssignments]);
  
  // Generic helper that returns translation if available; otherwise uses language-specific fallback
  const tf = (key, en, fa, ps) => {
    let res;
    try {
      res = t(key);
    } catch {
      res = undefined;
    }
    if (res && typeof res === 'string' && res !== key) return res;

    const lang = i18n.language || 'en';
    if (lang.startsWith('fa')) return fa || en;
    if (lang.startsWith('ps')) return ps || en;
    return en;
  };
  
  // Helper function to get documents array regardless of API response structure
  const getDocuments = () => {
    if (!documentsData) return [];
    if (documentsData.documents && Array.isArray(documentsData.documents)) {
      return documentsData.documents;
    }
    if (documentsData.data && Array.isArray(documentsData.data)) {
      return documentsData.data;
    }
    if (Array.isArray(documentsData)) {
      return documentsData;
    }
    return [];
  };
  
  // Helper function to get task assignments
  const getTaskAssignments = () => {
    if (!taskAssignmentsData) return [];
    if (taskAssignmentsData.assignments && Array.isArray(taskAssignmentsData.assignments)) return taskAssignmentsData.assignments;
    if (taskAssignmentsData.data && Array.isArray(taskAssignmentsData.data)) return taskAssignmentsData.data;
    if (Array.isArray(taskAssignmentsData)) return taskAssignmentsData;
    return [];
  };
  
  // Filter out documents that are already assigned to tasks or are in excludedDocumentIds
  const getAvailableDocuments = () => {
    const allDocuments = getDocuments();
    const taskAssignments = getTaskAssignments();
    // Create a Set of document IDs that are already assigned to tasks
    const assignedDocumentIds = new Set();
    taskAssignments.forEach(assignment => {
      if (assignment.document_id) {
        assignedDocumentIds.add(assignment.document_id);
      }
    });
    // Add excludedDocumentIds to the set
    excludedDocumentIds.forEach(id => assignedDocumentIds.add(id));
    // Filter out documents that are already assigned or excluded
    return allDocuments.filter(document => !assignedDocumentIds.has(document.id));
  };
  
  const handleSubmit = () => {
    // Validate selection
    if (!documentId) {
      setError(t('documents:error_select_document', 'مهرباني وکړئ یو سند وټاکئ'));
      return;
    }
    
    // Call the onDocumentSelected with the selection
    onDocumentSelected(documentId);
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <RTLDialogTitle {...getRTLProps('dialogtitle')}>
        {tf('tasks:select_document', 'Select Document', 'انتخاب سند', 'د سند ټاکنه')}
      </RTLDialogTitle>
      <RTLDialogContent {...getRTLProps('dialogcontent')}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ my: 2 }}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            gutterBottom
            sx={{ textAlign: isRTL ? 'right' : 'left' }}
          >
            {tf('tasks:select_document_description', 'Select a document to assign a task', 'لطفاً یک سند برای تخصیص سند انتخاب کنید', 'مهرباني وکړئ د سندونه لپاره یو سند وټاکئ')}
          </Typography>
        </Box>
        
        {/* Document Selection */}
        <Typography 
          variant="subtitle2" 
          gutterBottom 
          sx={{ 
            mt: 2,
            textAlign: isRTL ? 'right' : 'left'
          }}
        >
          {tf('tasks:document_selection', 'Document Selection', 'انتخاب سند', 'د سند ټاکنه')}
        </Typography>
        
        {documentsLoading || taskAssignmentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {tf('common:loading', 'Loading...')}
            </Typography>
          </Box>
        ) : (
          <>
            {getDocuments().length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {tf('documents:no_documents', 'No documents available. Please create a document first.', 'هیچ سندی موجود نیست. لطفاً اول یک سند ایجاد کنید.', 'هیڅ سند شتون نلري. مهرباني وکړئ لومړی یو سند جوړ کړئ.')}
              </Alert>
            )}
            {getDocuments().length > 0 && getAvailableDocuments().length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {tf('tasks:all_documents_assigned', 'All documents are already assigned to tasks. Create a new document to assign.', 'تمام اسناد قبلاً به اسناد اختصاص داده شده اند. برای اختصاص یک سند جدید ایجاد کنید.', 'ټول اسناد لا دمخه دندو ته ټاکل شوي دي. د ټاکلو لپاره نوی سند جوړ کړئ.')}
              </Alert>
            )}
          </>
        )}
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <RTLFormControl fullWidth {...getRTLProps('select')}>
              <InputLabel>
                {tf('documents:document', 'Document', 'سند', 'سند')}
              </InputLabel>
              <Select
                value={documentId}
                label={tf('documents:document', 'Document', 'سند', 'سند')}
                onChange={(e) => setDocumentId(e.target.value)}
                disabled={documentsLoading}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: isRTL ? 'right' : 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: isRTL ? 'right' : 'left',
                  },
                }}
              >
                {documentsLoading || taskAssignmentsLoading ? (
                  <MenuItem disabled>{tf('common:loading', 'Loading...', 'در حال بارگذاری...', 'درلودل ...')}</MenuItem>
                ) : getAvailableDocuments().length > 0 ? (
                  getAvailableDocuments().map((doc) => (
                    <MenuItem key={doc.id} value={doc.id}>
                      {doc.title || t('documents:untitled', 'Untitled')} {doc.reference_number ? `(${doc.reference_number})` : ''}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>{tf('documents:no_documents_available', 'No documents available', 'هیچ سندی موجود نیست', 'هیڅ سند شتون نلري')}</MenuItem>
                )}
              </Select>
            </RTLFormControl>
          </Grid>
        </Grid>
      </RTLDialogContent>
      <RTLDialogActions {...getRTLProps('dialogactions')}>
        <ActionButton
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{ minWidth: '100px' }}
        >
          {tf('common:cancel', 'Cancel', 'لغو', 'لغو')}
        </ActionButton>
        <ActionButton
          variant="contained"
          color="primary"
          startIcon={<DescriptionIcon />}
          onClick={handleSubmit}
          disabled={documentsLoading || !documentId}
          sx={{ minWidth: '120px' }}
        >
          {tf('common:select', 'Select', 'انتخاب', 'انتخاب')}
        </ActionButton>
      </RTLDialogActions>
    </Dialog>
  );
};

export default DocumentSelectionDialog; 